import React, { useEffect, useMemo, useState } from 'react';
import type { Key } from 'react';

import { useHistory, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';

import type { SortingState } from '@tanstack/react-table';

import { deleteCampaign, getCampaigns } from 'Requests';
import { Button } from '@eltoro-ui/components';

import { usePermissionContext } from 'contexts/PermissionContext';

import Table from 'Components/Table';
import { EmptyStatus, PageHeader, ReactTableSearchBar } from 'Components';
import RenameOrderlineModal from 'Components/RenameOrderlineModal';
import DeactivateInfo from 'Pages/MyProfile/components/DeactivateAccount/components/DeactivateInfo';
import CampaignsList from 'Pages/Orderlines/CampaignsList';

import { getOrderlineColumns, redirectCampaign, redirectOrderline } from 'Pages/Orderlines/helpers';

import { getOrderlines, getTotalCounts } from 'Requests/Request_Methods/campaignMethods';

import { Tabs } from 'enums';
import type { TCampaign, TOrderline, TResPagination } from 'types';

import './Orderlines.scss';

export default function Orderlines() {
  const location = useLocation();
  const history = useHistory();

  const { userIsDeactivatedWithActiveOrderlines, userIsFailed, userIsActive } =
    usePermissionContext();

  const [campaigns, setCampaigns] = useState<TCampaign[]>([]);
  const [orderlines, setOrderlines] = useState<TOrderline[]>([]);
  const [totals, setTotals] = useState({ orderine: 0, camapign: 0 });
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
  const [deleteCampaignData, setDeleteCampaignData] = useState<TCampaign[]>([]);
  const [deleteIsLoading, setDeleteIsLoading] = useState(false);

  const [campaignPage, setCampaignPage] = useState<number>(1);
  const [orderlinePage, setOrderlinePage] = useState<number>(1);
  const [orderlineSorting, setOrderlineSorting] = useState<SortingState>([]);

  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState<TCampaign[]>([]);
  const [campaignPagination, setCampaignPagination] = useState<TResPagination>({
    total_count: 0,
    total_pages: 0,
  });
  const [orderlinePagination, setOrderlinePagination] = useState<TResPagination>({
    total_count: 0,
    total_pages: 0,
  });
  const [campaignSorting, setCampaignSorting] = useState<SortingState>([]);

  const [fetchDataIsLoading, setFetchDataIsLoading] = useState(false);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [currentOrderline, setCurrentOrderline] = useState<null | TOrderline>(null);

  const [searchValue, setSearchValue] = useState('');

  const filter = new URLSearchParams(location.search).get('filter');
  const searchPlaceholder =
    location.pathname.includes(Tabs.ORDERLINES) && !filter
      ? 'Search by orderline'
      : 'Search by campaign';

  const handleSearchValue = (value: string) => {
    setSearchValue(value);
    if (location.pathname.includes(Tabs.ORDERLINES) && !filter) {
      setOrderlineSorting([]);
      setOrderlinePage(1);
    } else {
      setCampaignSorting([]);
      setCampaignPage(1);
    }
  };
  const _fetchCampaignData = async () => {
    setFetchDataIsLoading(true);
    try {
      const [sort] = campaignSorting;
      let sort_key: string | null = null;
      let order: string | null = null;

      if (sort) {
        sort_key = sort.id;
        order = sort.desc ? 'desc' : 'asc';
      }

      const res = await getCampaigns(campaignPage, 50, searchValue, sort_key, order);
      if (res.data) {
        const { total_campaigns, total_pages, campaigns } = res.data;
        setCampaigns(campaigns);
        setCampaignPagination({ total_count: total_campaigns, total_pages });
      }
      setFetchDataIsLoading(false);
    } catch (err: any) {
      toast.error(err.detail);
      setFetchDataIsLoading(false);
    }
  };

  const _fetchTabsTotalCounts = async () => {
    const res = await getTotalCounts();
    if (res.data)
      setTotals({ orderine: res.data.orderline_count, camapign: res.data.campaign_count });
  };
  const _fetchOrderlineData = async () => {
    setFetchDataIsLoading(true);
    try {
      const [sort] = orderlineSorting;
      let sort_key: string | null = null;
      let order: string | null = null;

      if (sort) {
        sort_key = sort.id;
        order = sort.desc ? 'desc' : 'asc';
      }

      const res = await getOrderlines(orderlinePage, 50, searchValue, sort_key, order);
      if (res.data) {
        const { total_orderlines, total_pages, orderlines } = res.data;
        setOrderlines(orderlines);
        setOrderlinePagination({ total_count: total_orderlines, total_pages });
      }
      setFetchDataIsLoading(false);
    } catch (err: any) {
      toast.error(err.detail || 'Something went wrong!');
      setFetchDataIsLoading(false);
    }
  };

  useEffect(() => {
    _fetchTabsTotalCounts();
    if (location.pathname.includes(Tabs.ORDERLINES) && filter) {
      _fetchCampaignData();
    } else {
      _fetchOrderlineData();
    }
  }, [
    campaignPage,
    orderlinePage,
    location.pathname,
    filter,
    location.state?.refreshData,
    searchValue,
    orderlineSorting?.[0],
    campaignSorting?.[0],
  ]);

  const onDelete = async (_items: TCampaign[]) => {
    setDeleteIsLoading(true);
    const ids = _items.map(item => item.id);

    try {
      await deleteCampaign({ campaign_id: ids });

      setDeleteModalIsOpen(false);
      setDeleteIsLoading(false);
      setSelectedRowKeys([]);
      setSelectedCampaigns([]);

      if (campaignPage === 1) await _fetchCampaignData();
      else await setCampaignPage(1);
    } catch (err: any) {
      toast.error(err.detail);
      setDeleteIsLoading(false);
    }
  };

  const handleOpenRenameModal = (open: boolean, orderline: TOrderline | null) => {
    setRenameModalOpen(open);
    setCurrentOrderline(orderline);
  };

  const changeTab = (url: string) => () => {
    if ((filter && url.includes('filter')) || (!filter && url === location.pathname)) return;

    if (location.pathname.includes(Tabs.ORDERLINES) && filter) {
      setCampaignSorting([]);
      setCampaignPage(1);
    } else {
      setOrderlineSorting([]);
      setOrderlinePage(1);
    }
    history.push(url);
    setSearchValue('');
  };
  const updateCreativeAfterEditOrderlineName = ({
    orderline_uuid,
    name,
  }: Pick<TOrderline, 'name' | 'orderline_uuid'>) =>
    setOrderlines(orderlines =>
      orderlines.map(orderline =>
        orderline.orderline_uuid === orderline_uuid ? { ...orderline, name } : { ...orderline }
      )
    );

  const changeRoutePath = (actionItem: TCampaign | TOrderline) => {
    if ('step' in actionItem) {
      history.push(redirectCampaign(actionItem));
    }
    if ('orderline_uuid' in actionItem) {
      history.push(redirectOrderline(actionItem));
    }
  };

  const newCampaignUrl = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.append('action', 'create-campaign');

    return `${location.pathname}?${searchParams.toString()}`;
  }, [location]);

  return (
    <div className="Campaigns">
      <PageHeader
        UNSAFE_CLASSNAME="Campaigns__PageHeader"
        LeftContent_ClassName="MyListings__header"
        subTitle={
          <span className="MyListings__totalCounts">
            You have{' '}
            {location.pathname.includes(Tabs.ORDERLINES) && filter ? (
              <b>
                {campaignPagination.total_count} campaign
                {campaignPagination.total_count > 1 ? 's' : ''}
              </b>
            ) : (
              <b>
                {orderlinePagination.total_count} orderline
                {orderlinePagination.total_count > 1 ? 's' : ''}
              </b>
            )}
          </span>
        }
        actions={[
          <ReactTableSearchBar
            key={searchPlaceholder}
            searchValue={searchValue}
            handleSearchValue={handleSearchValue}
            placeholder={searchPlaceholder}
          />,
          <Button
            key="new-campaign"
            kind="primary"
            to={newCampaignUrl}
            replace
            size="l"
            weight="bold"
            disabled={userIsDeactivatedWithActiveOrderlines || userIsFailed}
          >
            New Campaign
          </Button>,
        ]}
      />
      <DeactivateInfo />
      <div className="table-tabs">
        <button
          onClick={changeTab('/orderlines')}
          className={location.pathname.includes(Tabs.ORDERLINES) && !filter ? 'active' : 'inactive'}
        >
          Orderlines ({totals.orderine})
        </button>
        <button
          onClick={changeTab('/orderlines?filter=campaigns')}
          className={filter === Tabs.CAMPAIGNS ? 'active' : 'inactive'}
        >
          Campaigns ({totals.camapign})
        </button>
      </div>
      {location.pathname.includes(Tabs.ORDERLINES) && !filter ? (
        <>
          <Table<TOrderline>
            rounded={false}
            columns={getOrderlineColumns(userIsActive, handleOpenRenameModal, changeRoutePath)}
            data={orderlines ?? []}
            loading={fetchDataIsLoading}
            sortConfig={{
              sorting: orderlineSorting,
              onSortingChange: setOrderlineSorting,
            }}
            pagination={{
              hideOnSinglePage: true,
              pageSize: 50,
              current: orderlinePage,
              total: orderlinePagination.total_count,
              totalPages: orderlinePagination.total_pages,
              onChange: page => setOrderlinePage(page),
            }}
            emptyText={
              <EmptyStatus
                heading={
                  searchValue && !orderlines.length
                    ? 'No results found'
                    : 'You don’t have any orderlines yet.'
                }
                subHeading={
                  searchValue && !orderlines.length
                    ? 'You might consider trying different keywords, double-checking for any spelling errors, or adjusting your filters.'
                    : 'Your orderline list will appear here once you create campaigns.'
                }
              />
            }
          />
          {renameModalOpen && currentOrderline && (
            <RenameOrderlineModal
              orderline_uuid={currentOrderline.orderline_uuid}
              name={currentOrderline.name}
              onSuccess={name => {
                updateCreativeAfterEditOrderlineName({
                  orderline_uuid: currentOrderline.orderline_uuid,
                  name,
                });
              }}
              onCancel={() => handleOpenRenameModal(false, null)}
            />
          )}
        </>
      ) : (
        <CampaignsList
          campaigns={campaigns ?? []}
          fetchDataIsLoading={fetchDataIsLoading}
          campaignPagination={campaignPagination}
          onDelete={onDelete}
          deleteCampaignData={deleteCampaignData}
          setDeleteCampaignData={setDeleteCampaignData}
          deleteModalIsOpen={deleteModalIsOpen}
          setDeleteModalIsOpen={setDeleteModalIsOpen}
          deleteIsLoading={deleteIsLoading}
          campaignPage={campaignPage}
          setCampaignPage={setCampaignPage}
          changeRoutePath={changeRoutePath}
          campaignSorting={campaignSorting}
          setCampaignSorting={setCampaignSorting}
          searchValue={searchValue}
          selectionRows={[selectedRowKeys, setSelectedRowKeys]}
          selectionCampaigns={[selectedCampaigns, setSelectedCampaigns]}
        />
      )}
    </div>
  );
}
